import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Check } from "lucide-react";
import api, { API } from "../lib/api";
import Seo from "../components/Seo";
import { categoryLabel } from "../components/ProjectCard";
import Reveal from "../components/Reveal";

const STAGES = [
  { key: "before", label: "Before" },
  { key: "during", label: "During Construction" },
  { key: "completed", label: "Completed" },
];

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stage, setStage] = useState("completed");

  useEffect(() => {
    setProject(null); setNotFound(false);
    api.get(`/projects/${id}`)
      .then((r) => {
        setProject(r.data);
        const stages = r.data.photos?.map((p) => p.stage) || [];
        setStage(stages.includes("completed") ? "completed" : stages[0] || "completed");
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-24 min-h-[60vh] bg-paper">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="font-display font-bold text-3xl text-ink">Project not found</h1>
          <p className="mt-3 text-slate2">This project may have been removed or isn't published yet.</p>
          <Link to="/projects" data-testid="project-notfound-back" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-golddark hover:text-ink transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Projects
          </Link>
        </div>
      </div>
    );
  }

  if (!project) {
    return <div className="pt-40 pb-24 min-h-[60vh] bg-paper"><div className="max-w-7xl mx-auto px-4"><div className="aspect-[16/9] bg-line/50 animate-pulse" /></div></div>;
  }

  const photos = project.photos || [];
  const availableStages = STAGES.filter((s) => photos.some((p) => p.stage === s.key));
  const stagePhotos = photos.filter((p) => p.stage === stage);

  return (
    <>
      <Seo title={`${project.name} | ${categoryLabel(project.category)} ${project.location || "Wellington"} — Razor Build`} description={project.description?.slice(0, 155) || `${project.name} — a Razor Build ${categoryLabel(project.category).toLowerCase()} project in ${project.location || "Wellington"}.`} />
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <Link to="/projects" data-testid="project-back-link" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white/60 hover:text-gold transition-colors">
            <ArrowLeft className="w-4 h-4" /> All Projects
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span className="bg-gold text-white font-mono text-[10px] tracking-[0.18em] uppercase px-3 py-1.5">{categoryLabel(project.category)}</span>
            {project.location && (
              <span className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.15em] uppercase text-white/70">
                <MapPin className="w-3.5 h-3.5 text-gold" /> {project.location}
              </span>
            )}
          </div>
          <h1 className="mt-4 font-display font-bold text-3xl md:text-5xl tracking-tight text-white display-expanded leading-[1.02] max-w-3xl">{project.name}</h1>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          {availableStages.length > 0 ? (
            <>
              <div data-testid="project-stage-tabs" className="flex flex-wrap gap-2 mb-8">
                {availableStages.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setStage(s.key)}
                    data-testid={`stage-tab-${s.key}`}
                    className={`font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors ${stage === s.key ? "bg-ink text-white border-ink" : "border-line text-slate2 hover:border-ink hover:text-ink bg-white"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                {stagePhotos.map((p, i) => (
                  <Reveal key={p.id} delay={(i % 2) * 0.06}>
                    <figure className="border border-line bg-white">
                      <img src={`${API}/media/${project.id}/${p.id}`} alt={p.caption || `${project.name} — ${stage} photo ${i + 1}`} loading={i > 1 ? "lazy" : "eager"} className="w-full aspect-[4/3] object-cover img-duotone" data-testid={`project-photo-${p.id}`} />
                      {p.caption && (
                        <figcaption className="px-4 py-3 text-xs text-slate2 leading-relaxed border-t border-line" data-testid={`project-photo-caption-${p.id}`}>
                          {p.caption}
                        </figcaption>
                      )}
                    </figure>
                  </Reveal>
                ))}
              </div>
            </>
          ) : (
            <p className="text-slate2 text-sm border border-line bg-white px-6 py-8">Photography for this project is being prepared.</p>
          )}

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            {project.description && (
              <Reveal className="lg:col-span-7">
                <h2 className="font-mono text-[11px] tracking-[0.25em] uppercase text-golddark">About This Project</h2>
                <p className="mt-4 text-slate2 leading-relaxed whitespace-pre-line">{project.description}</p>
              </Reveal>
            )}
            {project.scope?.length > 0 && (
              <Reveal delay={0.1} className="lg:col-span-5">
                <div className="bg-white border border-line p-7">
                  <h2 className="font-mono text-[11px] tracking-[0.25em] uppercase text-golddark">Scope of Work</h2>
                  <ul className="mt-5 space-y-3">
                    {project.scope.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-ink/85">
                        <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>

          <Reveal delay={0.05}>
            <div className="mt-14 bg-ink text-white px-8 py-10 md:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="font-display font-bold text-2xl md:text-3xl display-expanded">Planning something similar?</h2>
                <p className="mt-2 text-white/60 text-sm">Tell us about your project — or send your plans straight through.</p>
              </div>
              <Link to="/contact" data-testid="project-detail-cta" className="inline-flex items-center gap-2.5 bg-gold hover:bg-golddark text-white font-mono text-[11px] tracking-[0.18em] uppercase px-6 py-4 transition-colors shrink-0">
                Start Your Project <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
